import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MoldOrder, MoldOrderFirstLevelType
} from '../../types/mold';
import MoldOrderCard from './MoldOrderCard';
import { useNavigate } from 'react-router-dom';
import { useFontSize, FontSizeKey } from '../../context/FontSizeContext';
import { List, type RowComponentProps } from 'react-window';
import { isFinalStep } from '../MoldDetail/utils';
import { BoardColumn } from '@/models/appState';
import { getDynamicIcon } from '@/utils/icon';

// Base card height for 'base' font size
const DEFAULT_CARD_HEIGHT = 185;
const DEFAULT_COMPLETE_CARD_HEIGHT = 240;

// Card height multipliers for different font sizes
const CARD_HEIGHT_MULTIPLIERS: Record<FontSizeKey, number> = {
  'xs': 0.85,
  'sm': 0.9,
  'base': 1.025,
  'lg': 1.2,
  'xl': 1.4,
  '2xl': 1.8,
};

const getDefaultCardHeight = (isCompleteStep: boolean) => {
  return isCompleteStep ? DEFAULT_COMPLETE_CARD_HEIGHT : DEFAULT_CARD_HEIGHT;
}

type KanbanRowProps = {
  orders: MoldOrder[];
  onCardClick: (order: MoldOrder) => void;
};

function Row({ index, style, orders, onCardClick }: RowComponentProps<KanbanRowProps>) {
  const order = orders[index];
  return (
    <div style={{ ...style, paddingBottom: '16px' }} className="px-2 py-2">
      <MoldOrderCard moldOrder={order} onClick={() => onCardClick(order)} />
    </div>
  );
}

interface KanbanColumnProps {
  column: BoardColumn;
  moldOrders: MoldOrder[];
  boardType?: MoldOrderFirstLevelType;
  displayMode?: 'tv' | 'default';
}
const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  moldOrders,
  boardType,
  displayMode = 'default',
}) => {
  const navigate = useNavigate();
  const { fontScale } = useFontSize();

  // Calculate initial list height based on display mode
  const getInitialListHeight = () => {
    if (displayMode === 'tv') {
      // For TV mode, account for:
      // - Header (~60px)
      // - Controls (~80px) 
      // - Optional padding (16px top when enabled)
      // - Buffer space (~40px)
      return Math.floor(window.innerHeight - 196);
    }
    return Math.floor(window.innerHeight * 0.70);
  };

  const [listHeight, setListHeight] = useState(getInitialListHeight());
  const isLastColumn = column.statusIds.some(statusId => isFinalStep(statusId));
  const defaultCardHeight = getDefaultCardHeight(isLastColumn);
  const [cardHeight, setCardHeight] = useState(defaultCardHeight);

  // Update card height when font scale or display mode changes.
  useEffect(() => {
    const multiplier = CARD_HEIGHT_MULTIPLIERS[fontScale];
    const newCardHeight = Math.round(defaultCardHeight * multiplier);
    setCardHeight(newCardHeight);
  }, [fontScale, defaultCardHeight]);

  // Update list height when display mode changes
  useEffect(() => {
    setListHeight(getInitialListHeight());
  }, [displayMode]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (displayMode === 'tv') {
        // For TV mode, account for header, controls, optional padding and buffer
        setListHeight(Math.floor(window.innerHeight - 196));
      } else {
        setListHeight(Math.floor(window.innerHeight * 0.7));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [displayMode]);

  // Stable callback/array so react-window's rowProps don't re-render every visible card each render.
  const onCardClick = useCallback((moldOrder: MoldOrder) => {
    const queryParams = new URLSearchParams();
    queryParams.append('source', 'kanban');
    if (boardType) {
      queryParams.append('boardType', boardType);
    }
    navigate(`/mold-orders/${moldOrder.id}?${queryParams.toString()}`);
  }, [navigate, boardType]);

  const filteredMoldOrders = useMemo(() => moldOrders
    .filter((moldOrder) => column.statusIds.includes(moldOrder.status))
    .sort((a, b) => {
      if (column.id?.toString() === 'completed') {
        return (
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      }
      return 0;
    }), [moldOrders, column.statusIds, column.id]);

  const StatusIcon = column.icon ? getDynamicIcon({ name: column.icon, source: 'lucide' }) as React.ComponentType<{ size?: number }> : null;

  // Get color scheme based on column's colorCode
  const getColorScheme = () => {
    const colorCode = column.colorCode || 'blue';

    const colorSchemes: Record<string, {
      headerGradient: string;
      iconBg: string;
      iconText: string;
      border: string;
      badgeBg: string;
      badgeText: string;
      contentBg: string;
    }> = {
      blue: {
        headerGradient: 'bg-gradient-to-r from-blue-50 to-sky-50',
        iconBg: 'bg-blue-500/10',
        iconText: 'text-blue-600',
        border: 'border-blue-200/60',
        badgeBg: 'bg-blue-500',
        badgeText: 'text-white',
        contentBg: 'bg-gradient-to-b from-blue-50/20 to-blue-50/40',
      },
      green: {
        headerGradient: 'bg-gradient-to-r from-green-50 to-emerald-50',
        iconBg: 'bg-green-500/10',
        iconText: 'text-green-600',
        border: 'border-green-200/60',
        badgeBg: 'bg-green-500',
        badgeText: 'text-white',
        contentBg: 'bg-gradient-to-b from-green-50/20 to-green-50/40',
      },
      yellow: {
        headerGradient: 'bg-gradient-to-r from-yellow-50 to-amber-50',
        iconBg: 'bg-yellow-500/10',
        iconText: 'text-yellow-600',
        border: 'border-yellow-200/60',
        badgeBg: 'bg-yellow-500',
        badgeText: 'text-white',
        contentBg: 'bg-gradient-to-b from-yellow-50/20 to-yellow-50/40',
      },
      amber: {
        headerGradient: 'bg-gradient-to-r from-amber-50 to-orange-50',
        iconBg: 'bg-amber-500/10',
        iconText: 'text-amber-600',
        border: 'border-amber-200/60',
        badgeBg: 'bg-amber-500',
        badgeText: 'text-white',
        contentBg: 'bg-gradient-to-b from-amber-50/20 to-amber-50/40',
      },
      red: {
        headerGradient: 'bg-gradient-to-r from-red-50 to-rose-50',
        iconBg: 'bg-red-500/10',
        iconText: 'text-red-600',
        border: 'border-red-200/60',
        badgeBg: 'bg-red-500',
        badgeText: 'text-white',
        contentBg: 'bg-gradient-to-b from-red-50/20 to-red-50/40',
      },
      purple: {
        headerGradient: 'bg-gradient-to-r from-purple-50 to-violet-50',
        iconBg: 'bg-purple-500/10',
        iconText: 'text-purple-600',
        border: 'border-purple-200/60',
        badgeBg: 'bg-purple-500',
        badgeText: 'text-white',
        contentBg: 'bg-gradient-to-b from-purple-50/20 to-purple-50/40',
      },
      gray: {
        headerGradient: 'bg-gradient-to-r from-gray-50 to-slate-50',
        iconBg: 'bg-gray-500/10',
        iconText: 'text-gray-600',
        border: 'border-gray-200/60',
        badgeBg: 'bg-gray-500',
        badgeText: 'text-white',
        contentBg: 'bg-gradient-to-b from-gray-50/20 to-gray-50/40',
      },
    };

    return colorSchemes[colorCode] || colorSchemes.blue;
  };

  const colors = getColorScheme();

  return (
    <div className="flex-1 min-w-[250px] flex flex-col h-full">
      <div className="bg-white rounded-t-lg border border-gray-200 overflow-hidden">
        <div className={`h-1 ${colors.badgeBg}`}></div>
        <div className="px-4 py-3 flex justify-between items-center">
          <div className="flex flex-row gap-2.5 items-center">
            {StatusIcon && (
              <div className={`${colors.iconText}`}>
                <StatusIcon size={20} />
              </div>
            )}
            <h3 className="font-semibold text-base text-gray-800">
              {column.title}
            </h3>
          </div>
          <div className={`px-2.5 py-1 ${colors.iconBg} ${colors.iconText} rounded-full text-sm font-bold`}>
            {filteredMoldOrders.length}
          </div>
        </div>
      </div>

      {/* Column content */}
      <div
        className="bg-gray-100 border-x border-gray-200 min-h-[150px] flex-1"
        style={{ padding: 0 }}
      >
        {filteredMoldOrders.length === 0 ? null : (
          <List
            rowComponent={Row}
            rowCount={filteredMoldOrders.length}
            rowHeight={cardHeight}
            rowProps={{ orders: filteredMoldOrders, onCardClick }}
            overscanCount={3}
            style={{ height: listHeight }}
          />
        )}
      </div>

      {/* Bottom border */}
      <div className="h-2 bg-white border-x border-b border-gray-200 rounded-b-lg"></div>
    </div>
  );
};

export default KanbanColumn;
